import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useSearchParams } from 'react-router';

import { itemSubstitutesQuery } from 'pages/disruption/api';
import { validateForDaysCalculated } from 'pages/disruption/queue/utils';
import { substitutionsBootstrapQuery } from 'pages/substitutes/api';
import type { SubListParameters, SubstituteStatusStates } from 'pages/substitutes/types';

const SUBSTITUTES_PAGE_SIZE = '100';

const APPROVED_SUBSTITUTE_STATUSES = new Set<SubstituteStatusStates>(['Approved', 'Completed']);

export function useEventItemApprovedSubstitutes(clientItemMasterId: string) {
  const [searchParams] = useSearchParams();
  const clinicalLocation = searchParams.get('for_clinical_location') || '';
  const clinicalLocationGroup = searchParams.get('for_clinical_location_group') || '';
  const forDaysCalculated = validateForDaysCalculated(searchParams.get('for_days_calculated'));

  const bootstrapQuery = useQuery({
    ...substitutionsBootstrapQuery(),
  });

  const approvedStatusIds = React.useMemo(
    () =>
      bootstrapQuery.data?.request_status_options
        .filter((option) => APPROVED_SUBSTITUTE_STATUSES.has(option.entity_string))
        .map((option) => option.entity_id) ?? [],
    [bootstrapQuery.data?.request_status_options]
  );

  const params = React.useMemo((): SubListParameters => {
    return {
      include_inactive_items: 'true',
      include_substitute_inventory: 'true',
      sort: 'priority|asc',
      page_size: SUBSTITUTES_PAGE_SIZE,
      for_days_calculated: forDaysCalculated,
      ...(clinicalLocation ? { for_clinical_location: clinicalLocation } : {}),
      ...(clinicalLocationGroup ? { for_clinical_location_group: clinicalLocationGroup } : {}),
      ...(approvedStatusIds.length > 0 ? { for_status: approvedStatusIds } : {}),
    };
  }, [approvedStatusIds, clinicalLocation, clinicalLocationGroup, forDaysCalculated]);

  const substitutesQuery = useQuery({
    ...itemSubstitutesQuery(clientItemMasterId, params, false),
    enabled: approvedStatusIds.length > 0,
  });

  return {
    items: substitutesQuery.data?.items ?? [],
    isLoading: bootstrapQuery.isLoading || (approvedStatusIds.length > 0 && substitutesQuery.isLoading),
    isError: bootstrapQuery.isError || substitutesQuery.isError,
  };
}
